<template>
  <section class="signature-page">
    <PageHeader
      eyebrow="Módulo de assinaturas"
      title="Assinaturas dinâmicas"
      description="Cadastre, organize e prepare assinaturas visuais com fundo transparente para uso nos certificados. As posições já ficam registradas para integração futura com o modelo de emissão."
    >
      <template #actions>
        <button class="secondary-button" type="button" @click="handleExport">
          Exportar backup
        </button>

        <button class="primary-button" type="button" @click="startCreate">
          Nova assinatura
        </button>
      </template>

      <template #metrics>
        <div class="metric-card">
          <span>Total</span>
          <strong>{{ signatures.length }}</strong>
          <small>assinatura(s) cadastrada(s)</small>
        </div>

        <div class="metric-card">
          <span>Armazenamento</span>
          <strong>IndexedDB</strong>
          <small>dados salvos neste navegador</small>
        </div>

        <div class="metric-card">
          <span>Status</span>
          <strong>Visual</strong>
          <small>preparado para assinatura validada</small>
        </div>
      </template>
    </PageHeader>

    <div class="signature-layout">
      <aside class="info-panel">
        <div class="info-card featured">
          <span>Como funciona</span>
          <strong>Cadastro local e reutilizável</strong>
          <p>
            As assinaturas ficam salvas no navegador atual. Depois, elas poderão
            ser selecionadas na emissão de certificados individuais ou em lote.
          </p>
        </div>

        <div class="info-card">
          <span>Próxima etapa</span>
          <strong>Uso no certificado</strong>
          <p>
            Após cadastrar, vamos habilitar o checkbox de assinaturas dinâmicas
            na geração do certificado.
          </p>
        </div>

        <div class="info-card">
          <span>Futuro</span>
          <strong>Serviço centralizado</strong>
          <p>
            A estrutura já nasce preparada para exportação, sincronização e uso
            por outros aplicativos.
          </p>
        </div>
      </aside>

      <main class="list-panel">
        <div class="panel-header">
          <div>
            <span>Banco local</span>
            <strong>Assinaturas cadastradas</strong>
          </div>

          <small>{{ signatures.length }} registro(s)</small>
        </div>

        <SignatureList
          :signatures="signatures"
          @edit="startEdit"
          @remove="handleRemove"
        />
      </main>
    </div>

    <Teleport to="body">
      <Transition name="drawer">
        <div v-if="showForm" class="drawer-overlay" @click.self="cancelForm">
          <aside class="signature-drawer">
            <div class="drawer-header">
              <div>
                <span>{{
                  editingSignature ? "Editar assinatura" : "Nova assinatura"
                }}</span>
                <strong>
                  {{
                    editingSignature
                      ? editingSignature.signer?.name
                      : "Cadastrar assinatura"
                  }}
                </strong>
                <p>
                  Preencha os dados do assinante, desenhe a assinatura e defina
                  a posição padrão para uso no certificado.
                </p>
              </div>

              <button class="drawer-close" type="button" @click="cancelForm">
                ×
              </button>
            </div>

            <SignaturePadForm
              :editing-signature="editingSignature"
              @saved="handleSaved"
              @cancel="cancelForm"
            />
          </aside>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup>
import { onMounted, ref } from "vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import SignaturePadForm from "@/components/signatures/SignaturePadForm.vue";
import SignatureList from "@/components/signatures/SignatureList.vue";
import {
  deleteSignature,
  exportSignatures,
  getSignatures,
  saveSignature,
} from "@/services/signatureStorage";

const signatures = ref([]);
const showForm = ref(false);
const editingSignature = ref(null);

async function loadSignatures() {
  signatures.value = await getSignatures();
}

function startCreate() {
  editingSignature.value = null;
  showForm.value = true;
}

function startEdit(signature) {
  editingSignature.value = signature;
  showForm.value = true;
}

function cancelForm() {
  editingSignature.value = null;
  showForm.value = false;
}

async function handleSaved(payload) {
  try {
    await saveSignature(payload);
    await loadSignatures();

    editingSignature.value = null;
    showForm.value = false;
  } catch (error) {
    console.error("Erro ao salvar assinatura:", error);
    alert(
      "Não foi possível salvar a assinatura. Verifique o console para mais detalhes."
    );
  }
}

async function handleRemove(signature) {
  const confirmed = window.confirm(
    `Deseja excluir a assinatura de ${signature.signer?.name}?`
  );

  if (!confirmed) return;

  try {
    await deleteSignature(signature.id);
    await loadSignatures();
  } catch (error) {
    console.error("Erro ao excluir assinatura:", error);
    alert("Não foi possível excluir a assinatura.");
  }
}

async function handleExport() {
  const backup = await exportSignatures();
  const blob = new Blob([JSON.stringify(backup, null, 2)], {
    type: "application/json",
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = `certificador-assinaturas-${new Date()
    .toISOString()
    .slice(0, 10)}.json`;

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}

onMounted(() => {
  loadSignatures();
});
</script>

<style scoped>
.signature-page {
  width: min(1240px, calc(100% - 2rem));
  margin: 0 auto;
  padding: 1.5rem 0 4rem;
}

.signature-layout {
  display: grid;
  grid-template-columns: 310px minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  align-items: start;
}

.info-panel {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.info-card,
.list-panel {
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(12px);
}

.info-card {
  padding: 1rem;
}

.info-card.featured {
  background: linear-gradient(
      135deg,
      rgba(17, 24, 39, 0.96),
      rgba(30, 41, 59, 0.9)
    ),
    radial-gradient(
      circle at top right,
      rgba(255, 122, 0, 0.2),
      transparent 18rem
    );
}

.info-card span,
.panel-header span,
.metric-card span {
  display: block;
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.info-card strong {
  display: block;
  margin-top: 0.4rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
}

.info-card p {
  margin: 0.55rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.65;
}

.info-card.featured span {
  color: #fbbf24;
}

.info-card.featured strong {
  color: #f8fafc;
}

.info-card.featured p {
  color: #cbd5e1;
}

.list-panel {
  min-height: 420px;
  padding: 1rem;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-header strong {
  display: block;
  margin-top: 0.3rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.05rem;
}

.panel-header small {
  color: #64748b;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.75rem;
}

.metric-card {
  border: 1px solid rgba(214, 222, 234, 0.88);
  border-radius: 18px;
  padding: 0.9rem;
  background: rgba(255, 255, 255, 0.72);
}

.metric-card strong {
  display: block;
  margin-top: 0.35rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.05rem;
}

.metric-card small {
  display: block;
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.72rem;
}

.primary-button,
.secondary-button {
  border: 0;
  border-radius: 15px;
  padding: 0.82rem 1rem;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.78rem;
  cursor: pointer;
  transition: 0.2s ease;
}

.primary-button {
  color: #111827;
  background: linear-gradient(135deg, #ffb020, #ff7a00);
  box-shadow: 0 16px 32px rgba(255, 122, 0, 0.24);
}

.secondary-button {
  color: #334155;
  background: #ffffff;
  border: 1px solid #d6deea;
}

.primary-button:hover,
.secondary-button:hover {
  transform: translateY(-1px);
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.54);
  backdrop-filter: blur(8px);
}

.signature-drawer {
  width: min(760px, 100%);
  height: 100vh;
  overflow-y: auto;
  padding: 1.25rem;
  background: linear-gradient(180deg, #f8fafc, #eef2f7),
    radial-gradient(
      circle at top right,
      rgba(255, 122, 0, 0.12),
      transparent 22rem
    );
  box-shadow: -24px 0 80px rgba(15, 23, 42, 0.24);
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 24px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.82);
}

.drawer-header span {
  display: block;
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.drawer-header strong {
  display: block;
  margin-top: 0.4rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.25rem;
}

.drawer-header p {
  max-width: 560px;
  margin: 0.45rem 0 0;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.6;
}

.drawer-close {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 14px;
  color: #111827;
  font-size: 1.5rem;
  cursor: pointer;
  background: #fff0dc;
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .signature-drawer,
.drawer-leave-to .signature-drawer {
  transform: translateX(40px);
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.22s ease;
}

.drawer-enter-active .signature-drawer,
.drawer-leave-active .signature-drawer {
  transition: transform 0.22s ease;
}

@media (max-width: 980px) {
  .signature-layout {
    grid-template-columns: 1fr;
  }

  .info-panel {
    display: grid;
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .signature-page {
    width: min(100% - 1rem, 1240px);
    padding-top: 1rem;
  }

  .signature-drawer {
    width: 100%;
  }
}
</style>
